// Mobile Roster screen — the team's players in the shared swipeable ratings table (PlayerTable):
// Contract (Salary / Length) → Physical → Shooting → Ratings, with a per-row Trade shortcut on
// your own team. A team-logo button on the cap line switches which team's roster you're viewing.
// Tap a player's name for the full profile (trade/cut live there). Free Agents and Trades tabs
// use the same table.

import { useState } from "react";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { useLocal } from "../util/local.ts";
import { useNoTradingAllowed } from "../util/noTradingAllowed.ts";
import { untradableMessage } from "../util/untradable.ts";
import { toWorker } from "../util/toWorker.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { helpers } from "../util/helpers.ts";
import { RosterTabs } from "../components/RosterTabs.tsx";
import { TeamLogoInline } from "../components/TeamLogoInline.tsx";
import { MobileNotice } from "../components/MobileNotice.tsx";
import { PlayerTable } from "../components/PlayerTable.tsx";
import type { View } from "../../common/types.ts";

const GmRoster = ({
	players,
	capSpace,
	salaryCap,
	numRosterSpots,
	t,
	tid,
	editable,
	season,
}: View<"roster">) => {
	useTitleBar({ title: "Roster" });
	const { teamInfoCache, userTid, lid } = useLocal([
		"teamInfoCache",
		"userTid",
		"lid",
	]);
	const noTradingAllowed = useNoTradingAllowed();

	const viewedTid = tid ?? userTid;
	const viewedTeam: any = t ?? (teamInfoCache as any[])[viewedTid];
	const logoTeam: any = (teamInfoCache as any[])[viewedTid] ?? {};

	const [menuOpen, setMenuOpen] = useState(false);
	const [notice, setNotice] = useState<string | null>(null);

	const openProfile = (pid: number) => {
		realtimeUpdate([], helpers.leagueUrl(["player", pid]));
	};
	const tradePlayer = async (pid: number) => {
		// Seed a fresh trade with this (own-roster) player on our side and open the mobile Trade
		// wizard. NOT actions.addToTradingBlock — that navigates to the desktop trading-block UI.
		// The invalid partner tid (-1) makes the trade view auto-pick a partner the user can change.
		await toWorker("main", "createTrade", [
			{
				tid: userTid,
				pids: [pid],
				dpids: [],
				pidsExcluded: [],
				dpidsExcluded: [],
			},
			{ tid: -1, pids: [], dpids: [], pidsExcluded: [], dpidsExcluded: [] },
		] as any);
		realtimeUpdate([], `/l/${lid}/trade`);
	};

	// Team switcher options — every team, A–Z by city (region), each links to its roster.
	const teamOpts = (teamInfoCache as any[])
		.map((tt, i) => ({
			tid: i,
			abbrev: tt?.abbrev as string | undefined,
			region: (tt?.region as string) ?? "",
			name: (tt?.name as string) ?? "",
		}))
		.filter((tt) => tt.abbrev)
		.sort((a, b) => a.region.localeCompare(b.region));

	const goToTeam = (abbrev: string, tid2: number) => {
		setMenuOpen(false);
		realtimeUpdate([], `/l/${lid}/roster/${abbrev}_${tid2}`);
	};

	return (
		<div className="gmr">
			<style>{styles}</style>
			<RosterTabs active="players" lid={lid} />

			<div className="gmr-caprow">
				<button
					className="gmr-teambtn"
					onClick={() => setMenuOpen((v) => !v)}
					title="Switch team"
				>
					<TeamLogoInline
						imgURL={logoTeam.imgURL}
						imgURLSmall={logoTeam.imgURLSmall}
						size={34}
					/>
					<span className="gmr-teambtn-caret">▾</span>
				</button>
				<div className="gmr-capblock">
					<div className="gmr-capline">
						<span className="gmr-capline-cap">${capSpace.toFixed(1)}M</span>
						<span className="gmr-capline-of">
							{" "}
							cap space of ${salaryCap.toFixed(1)}M
						</span>
						<span className="gmr-capline-spots">
							{" "}
							· {numRosterSpots} open{" "}
							{Math.abs(numRosterSpots) === 1 ? "spot" : "spots"}
						</span>
					</div>
					{typeof viewedTeam?.ovr === "number" ? (
						<div className="gmr-rating">
							<span className="gmr-rating-label">OVERALL</span>
							<span className="gmr-rating-val">{viewedTeam.ovr}</span>
							{typeof viewedTeam?.pot === "number" ? (
								<>
									<span className="gmr-rating-label gmr-rating-sep">
										POTENTIAL
									</span>
									<span className="gmr-rating-val gmr-rating-pot">
										{viewedTeam.pot}
									</span>
								</>
							) : null}
						</div>
					) : null}
					<div className="gmr-intro">
						{viewedTeam ? `${viewedTeam.region} ${viewedTeam.name}` : "Roster"}{" "}
						· {(players as any[]).length} players ·{" "}
						{editable
							? noTradingAllowed
								? "tap a name to cut"
								: "tap a name to trade or cut"
							: "tap a name for the profile"}
					</div>
				</div>
			</div>

			{menuOpen ? (
				<div className="gmr-teammenu">
					{teamOpts.map((tt) => (
						<button
							key={tt.tid}
							className={`gmr-teamitem ${tt.tid === viewedTid ? "active" : ""}`}
							onClick={() => goToTeam(tt.abbrev as string, tt.tid)}
						>
							{tt.tid === viewedTid ? "✓ " : ""}
							{tt.region} {tt.name}
						</button>
					))}
				</div>
			) : null}

			<PlayerTable
				players={players as any[]}
				season={season}
				onName={(p) => openProfile(p.pid)}
				action={
					editable && !noTradingAllowed
						? (p) => (
								<button
									className="pt-actbtn"
									onClick={() => {
										// A recently-signed player can't be added to a trade — the worker
										// silently drops them, so explain why instead of opening an empty deal.
										const msg = untradableMessage(p);
										if (msg) {
											setNotice(msg);
											return;
										}
										tradePlayer(p.pid);
									}}
								>
									Trade
								</button>
							)
						: undefined
				}
			/>
			<MobileNotice
				message={notice}
				title="Can't trade this player"
				onClose={() => setNotice(null)}
			/>
		</div>
	);
};

const styles = `
.gmr { max-width:760px; margin:0 auto; padding:16px 16px 40px; color:#fff;
  font-family:Montserrat,system-ui,sans-serif; }
.gmr-caprow { display:flex; align-items:center; gap:12px; margin:8px 0 6px; }
.gmr-teambtn { display:flex; align-items:center; gap:3px; background:#181818; border:1px solid #262626;
  border-radius:12px; padding:6px 8px 6px 6px; cursor:pointer; flex:none; }
.gmr-teambtn-caret { color:#7f8b93; font-size:11px; }
.gmr-capblock { min-width:0; }
.gmr-capline { font-size:13px; font-weight:600; }
.gmr-capline-cap { color:#cfe0ec; font-weight:800; }
.gmr-capline-of, .gmr-capline-spots { color:#7f8b93; }
.gmr-rating { display:flex; align-items:baseline; gap:6px; margin-top:4px; }
.gmr-rating-label { font-size:9px; font-weight:900; letter-spacing:.6px; color:#7f8b93; }
.gmr-rating-sep { margin-left:8px; }
.gmr-rating-val { font-size:15px; font-weight:800; color:#cfe0ec; }
.gmr-rating-pot { color:#18E0A8; }
.gmr-intro { color:#8a8a8a; font-size:12px; margin-top:2px; }
.gmr-teammenu { background:#151515; border:1px solid #262626; border-radius:12px; padding:6px;
  margin:0 0 8px; max-height:280px; overflow-y:auto; }
.gmr-teamitem { display:block; width:100%; text-align:left; background:none; border:none;
  color:#cfe0ec; font-family:inherit; font-size:13px; font-weight:700; padding:9px 10px;
  border-radius:8px; cursor:pointer; }
.gmr-teamitem:hover { background:#1e1e1e; }
.gmr-teamitem.active { color:#18E0A8; }
`;

export default GmRoster;
